import { atom, useSetRecoilState } from "recoil";
import { gameStreamQueryKey } from "../lib/refresh";
import { fetchGameStream } from "../monitor/Monitor.state";
import { useInfiniteQuery, useMutation, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { alertState } from "../common/AlertDisplay/AlertDisplay.state";
import { shuffleArray } from "../lib/array";
import { unlockCodeState } from "../monitor/Monitor.state";
import { useRecoilState } from "recoil";
import { useTranslation } from "react-i18next";

export const featuredState = atom({
  key: "games/slideshow/settings/featured",
  default: false,
});

export const playingState = atom({
  key: "games/slideshow/settings/playing",
  default: false,
});

export const shuffleState = atom({
  key: "games/slideshow/settings/shuffle",
  default: 0,
});

export const fetchSlideshow =
  (game_id, code) =>
  ({ pageParam }) =>
    fetchGameStream(game_id, code, pageParam);

export const useSlideshowDataQuery = (game_id, validationState) => {
  const { t } = useTranslation();
  const [code] = useRecoilState(unlockCodeState);
  const [, setIsValidated] = validationState;
  const setAlert = useSetRecoilState(alertState);
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery(gameStreamQueryKey(game_id, code), fetchSlideshow(game_id, code), {
      getNextPageParam: (lastPage = {}, pages = []) => {
        const ids = (lastPage?.answers || []).map(({ id }) => parseInt(id));
        const { count } = lastPage;
        const loaded = pages.reduce(
          (sum, page) => (sum += page?.answers?.length),
          0
        );

        if ((lastPage && (lastPage?.answers || []).length === 0) || count <= loaded) {
          return undefined;
        }

        return Math.min(...ids);
      },
      suspense: true,
      useErrorBoundary: false,
      onError: () => {
        const message = t('Invalid code. Please try again.');
        setAlert({ type: "error", message, closable: true });
        setIsValidated(false);
      },
  });

  const types = [1, 2, 3, 9, 10];
  const answers = (data?.pages || [])
    .flatMap((page) => page?.answers)
    .filter((item = {}) => {
      const { sub_type, type } = item
      return type === 2 && sub_type === 2 ? false : types.includes(type);
    });
  // counts of featured submission is attached as `featured` property.
  // need to extract from 1 sample to get the information
  const featuredSubmissions =  (data?.pages || [])?.[0]?.featured
  return { answers, featuredSubmissions, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading };
};

export const useShuffleSlideshowMutation = (game_id) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => true, // No server-side shuffle, just update client state
    onMutate: async (shuffle = true) => {
      await queryClient.cancelQueries(gameStreamQueryKey(game_id));
      const previousData = queryClient.getQueryData(gameStreamQueryKey(game_id));

      if (!shuffle) {
        // When unshuffling, refetch original data from server
        await queryClient.invalidateQueries(gameStreamQueryKey(game_id));
        await queryClient.refetchQueries(gameStreamQueryKey(game_id));
        return { previousData };
      }

      // When shuffling, update client state
      if (previousData && previousData.pages) {
        const allAnswers = previousData.pages.flatMap((page) => page.answers || []);
        const answers = shuffleArray(allAnswers);

        // Put shuffled answers into the first page, others empty
        const pages = [
          { ...previousData.pages[0], answers, featured: previousData.pages[0]?.featured },
          ...previousData.pages.slice(1).map((page) => ({ ...page, answers: [] })),
        ];

        queryClient.setQueryData(gameStreamQueryKey(game_id), {
          ...previousData,
          pages,
        });
      }

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(gameStreamQueryKey(game_id), context.previousData);
      }
    },
  });
};



export const useSlideshowQuery = ({ validationState }) => {
  const { game_id } = useParams();
  return useSlideshowDataQuery(game_id, validationState);
};

export const useSlideshowLoadingQuery = ({ validationState }) => {
  const { game_id } = useParams();
  const { isLoading } = useSlideshowDataQuery(game_id, validationState);

  return isLoading;
};
